import { useCallback, useEffect, useState } from "react";

const KEY = "cb_theme";
export const MODES = ["light", "dark", "auto"];

export function applyTheme(theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/** "38.9167, -0.1167" of "38.9167,-0.1167" → [lat, lon] */
export function parseGps(gps) {
  const m = String(gps || "").match(/(-?\d+[.,]?\d*)\s*[,;/]\s*(-?\d+[.,]?\d*)/);
  if (!m) return null;
  const lat = parseFloat(m[1].replace(",", "."));
  const lon = parseFloat(m[2].replace(",", "."));
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lat, lon] : null;
}

/** Zonsopgang en zonsondergang (lokale tijd, in minuten na middernacht) — NOAA-benadering. */
export function sunTimes(date, lat, lon) {
  const rad = Math.PI / 180;
  const start = new Date(date.getFullYear(), 0, 0);
  const day = Math.floor((date - start) / 86400000);
  const gamma = ((2 * Math.PI) / 365) * (day - 1 + 0.5);
  const eqTime = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const decl = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const cosH = Math.cos(90.833 * rad) / (Math.cos(lat * rad) * Math.cos(decl)) - Math.tan(lat * rad) * Math.tan(decl);
  if (cosH > 1 || cosH < -1) return null; // poolnacht of poolzomer
  const ha = Math.acos(cosH) / rad;
  const tzOffset = -date.getTimezoneOffset(); // minuten t.o.v. UTC
  return {
    sunrise: 720 - 4 * (lon + ha) - eqTime + tzOffset,
    sunset: 720 - 4 * (lon - ha) - eqTime + tzOffset,
  };
}

/** Donker tussen zonsondergang en zonsopgang; zonder GPS valt het terug op 21:00–07:00. */
export function isNight(gps, now = new Date()) {
  const minutes = now.getHours() * 60 + now.getMinutes();
  const coords = parseGps(gps);
  const sun = coords ? sunTimes(now, coords[0], coords[1]) : null;
  if (!sun) return minutes >= 21 * 60 || minutes < 7 * 60;
  return minutes >= sun.sunset || minutes < sun.sunrise;
}

export function resolveTheme(mode, gps) {
  if (mode === "auto") return isNight(gps) ? "dark" : "light";
  return mode === "dark" ? "dark" : "light";
}

export function useTheme(gps) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(KEY);
    return MODES.includes(saved) ? saved : "light";
  });

  useEffect(() => {
    localStorage.setItem(KEY, mode);
    applyTheme(resolveTheme(mode, gps));
    if (mode !== "auto") return;
    const id = setInterval(() => applyTheme(resolveTheme(mode, gps)), 300000);
    return () => clearInterval(id);
  }, [mode, gps]);

  const cycle = useCallback(() => setMode((m) => MODES[(MODES.indexOf(m) + 1) % MODES.length]), []);
  return { mode, theme: resolveTheme(mode, gps), toggle: cycle };
}
